<template>
  <v-container
    fluid
    tag="section"
  >
    <v-card>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model="search"
              clearable
              outlined
              dense
              hide-details
              label="Субъект (subject_ref)"
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="applySearch"
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-btn
              color="primary"
              outlined
              @click="$refs.filter.open()"
            >
              <v-icon class="ml-3">
                mdi-filter-variant
              </v-icon>
              <span class="mx-3">Фильтры</span>
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            class="d-sm-flex justify-end"
          >
            <v-btn
              icon
              title="Настройка столбцов"
              @click="openSkudHeaders()"
            >
              <v-icon>mdi-view-column</v-icon>
            </v-btn>
            <v-btn
              icon
              title="Обновить"
              @click="reset(); load()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <!-- ponytail: клиентская сортировка/пагинация только по загруженным N; серверный paging — когда бэкенд добавит page/total -->
        <v-data-table
          :headers="headers.filter(a => a.show || a.active)"
          :items="items"
          :loading="loading"
          :footer-props="datatable_footer"
          no-data-text="Нет данных для отображения"
          @click:row="openView"
        >
          <template v-slot:footer.page-text="{pageStart,pageStop,itemsLength}">
            {{ pageStart }}-{{ pageStop }} из {{ itemsLength }}
          </template>
          <template v-slot:item.offline="{item}">
            <v-chip
              v-if="item.offline"
              small
              color="grey"
              text-color="white"
            >
              офлайн
            </v-chip>
          </template>
          <template v-slot:item.suspicious="{item}">
            <v-chip
              v-if="item.suspicious"
              small
              color="red"
              text-color="white"
            >
              подозрительный
            </v-chip>
          </template>
          <template v-slot:item.direction_mismatch="{item}">
            <v-chip
              v-if="item.direction_mismatch"
              small
              color="orange"
              text-color="white"
            >
              расхождение
            </v-chip>
          </template>
          <template v-slot:item.actions="{item}">
            <skud-actions-menu>
              <v-list-item @click="openView(item)">
                <v-list-item-content>
                  Открыть
                </v-list-item-content>
              </v-list-item>
            </skud-actions-menu>
          </template>
        </v-data-table>
        <div
          v-if="next_cursor_id"
          class="text-center mt-3"
        >
          <v-btn
            color="primary"
            outlined
            :loading="loading_more"
            @click="load(true)"
          >
            Загрузить ещё
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <skud-filter-dialog
      ref="filter"
      :fields="filterFields"
      storage-key="skud.passage"
      @apply="reset(); load()"
    />

    <v-dialog
      v-model="dialog_headers.show"
      persistent
      max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Настройка столбцов</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :items-per-page="10"
            :items="headers.filter(a => !a.show)"
            :headers="[{value: 'text', text: 'Название столбца'}, {value: 'active_temp', text: 'Отображение'}]"
            :footer-props="datatable_footer"
            no-data-text="Нет данных для отображения"
          >
            <template v-slot:item.active_temp="{item}">
              <v-checkbox v-model="item.active_temp" />
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="saveSkudHeaders('skud_passage')"
          >
            <span class="mx-4">Сохранить</span>
          </v-btn>
          <v-btn
            color="secondary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="dialog_headers.show = false"
          >
            <span class="mx-4">Закрыть</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <skud-passage-view-dialog ref="view" />
  </v-container>
</template>

<script>
import SkudFilterDialog from '@/views/skud/components/SkudFilterDialog.vue'
import SkudPassageViewDialog from '@/views/skud/components/SkudPassageViewDialog.vue'
import SkudActionsMenu from '@/views/skud/components/SkudActionsMenu.vue'
import { headersMixin } from '@/views/skud/shared/modules/headers-mixin'
import { SkudPassageService } from '@/views/skud/shared/services/skud-passage-service'
import { Method, PassageDirection, enumOptions } from '@/views/skud/shared/models/enums'

export default {
  name: 'SkudPassageList',
  components: {
    SkudFilterDialog,
    SkudPassageViewDialog,
    SkudActionsMenu
  },
  mixins: [headersMixin],
  data () {
    return {
      svc: null,
      items: [],
      loading: false,
      loading_more: false,
      search: '',
      next_cursor_occurred_at: null,
      next_cursor_id: null,
      datatable_footer: {
        itemsPerPageText: 'Показывать по',
        pageText: 'из',
        itemsPerPageOptions: [5, 10, 15, 30, 50]
      },
      headers: [
        { text: 'Действия', value: 'actions', show: true, sortable: false },
        { text: 'Дата/время', value: 'occurred_at', show: true },
        { text: 'Субъект', value: 'subject_ref', show: true },
        { text: 'Метод', value: 'method_text', show: true },
        { text: 'Место', value: 'place_name', active: true, active_temp: true },
        { text: 'Точка', value: 'point_name', active: true, active_temp: true },
        { text: 'ID прохода', value: 'public_id', active: true, active_temp: true },
        { text: 'Экран', value: 'screen', active: true, active_temp: true },
        { text: 'Экран (пересмотр)', value: 'revised_screen', active: true, active_temp: true },
        { text: 'Причина', value: 'reason', active: true, active_temp: true },
        { text: 'Гостевой пропуск', value: 'guest_pass_public_id', active: true, active_temp: true },
        { text: 'Офлайн', value: 'offline', active: true, active_temp: true },
        { text: 'Подозрительный', value: 'suspicious', active: true, active_temp: true },
        { text: 'Расхождение направления', value: 'direction_mismatch', active: true, active_temp: true }
      ],
      filterFields: {
        period: {
          index: 1,
          name: 'Период',
          data: [
            { name: 'С', field: 'from', type: 'date' },
            { name: 'По', field: 'to', type: 'date' }
          ]
        },
        event: {
          index: 2,
          name: 'Событие',
          data: [
            { name: 'Экран', field: 'screen', type: 'text' },
            { name: 'Метод', field: 'method', type: 'select', select: enumOptions(Method) },
            { name: 'Направление', field: 'direction', type: 'select', select: enumOptions(PassageDirection) },
            { name: 'Точка (point_id)', field: 'point_id', type: 'text' },
            { name: 'Причина', field: 'reason', type: 'text' },
            { name: 'Офлайн', field: 'offline', type: 'bool' },
            { name: 'Пересмотренные', field: 'revised', type: 'bool' },
            { name: 'Подозрительные', field: 'suspicious', type: 'bool' }
          ]
        },
        subject: {
          index: 3,
          name: 'Субъект',
          data: [
            { name: 'Субъект (subject_ref)', field: 'subject_ref', type: 'text' },
            { name: 'Группа (group_ref)', field: 'group_ref', type: 'text' }
          ]
        }
      }
    }
  },
  mounted () {
    this.svc = new SkudPassageService()
    this.setDefaultSkudHeaders(this.$store.state.user.skud_passage_headers)
    this.load()
  },
  methods: {
    reset () {
      this.items = []
      this.next_cursor_occurred_at = null
      this.next_cursor_id = null
    },
    applySearch () {
      this.reset()
      this.load()
    },
    async load (append) {
      const filters = Object.assign({}, this.$refs.filter.current())
      if (this.search) filters.subject_ref = this.search
      const cursors = append
        ? { next_cursor_occurred_at: this.next_cursor_occurred_at, next_cursor_id: this.next_cursor_id }
        : null
      if (append) this.loading_more = true
      else this.loading = true
      const res = await this.svc.getPassages(null, filters, cursors)
      this.loading = false
      this.loading_more = false
      if (this.svc.error) {
        this.$store.commit('snackbar', this.svc.error)
        return
      }
      this.items = append ? this.items.concat(res.items) : res.items
      this.next_cursor_occurred_at = res.next_cursor_occurred_at
      this.next_cursor_id = res.next_cursor_id
    },
    openView (item) {
      this.$refs.view.open(item.public_id, item.place_id)
    }
  }
}
</script>
